import { FC, useContext, useEffect, useRef, useState } from "react";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";
import { ProviderProfileFlyoutTable } from "@app/projects/network-assessment/provider-hub/provider-profile/ProviderProfileFlyoutTable";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { ProviderProfileFlyoutTableData } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileFlyoutTableInterfaces";
import { isNullOrUndefined } from "@app/shared/utility/isNullOrUndefined";
import { AccessGapTypeEnumToDisplay, formatSpecialtyWithCode, GeoInfoToDisplay, RegionSetStatusToDisplay, regionSetStatusSortRank } from "@app/projects/network-assessment/provider-hub/utility";
import { AccessGapTypeEnum } from "@app/projects/network-assessment/provider-hub/enumerators";

interface KeyPointsFlyoutProps {
  gapType: AccessGapTypeEnum.PartiallyFillableGap | AccessGapTypeEnum.FillableBySingleProvider;
}

export const KeyPointsFlyout: FC<KeyPointsFlyoutProps> = (props: KeyPointsFlyoutProps) => {
  const projectContext = useContext(CountyProjectContext);
  const providerHubContext = useContext(ProviderHubContext);
  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState<ProviderProfileFlyoutTableData[]>([]);
  const providerHubService = useRef(new ProviderHubService()).current;

  useEffect(() => {
    const accessPoints = providerHubService.getProviderServicingAccessPointsByAccessGapTypeAndContractEntityAsync(projectContext?.project?.projectId, providerHubContext.selectedContractEntity?.projectRunConfigurationId ?? providerHubContext.selectedOrganization?.projectRunConfigurationId, providerHubContext.selectedContractEntity?.contractEntityId ?? providerHubContext.selectedContractEntity?.contractEntityId, props.gapType);

    Promise.resolve(accessPoints).then((response) => {
      // Default sort: current service-area rows (Active/Pending/Approved) first, Opportunity below.
      const sortedAccessPoints = [...response].sort(
        (a, b) => regionSetStatusSortRank(a.regionSetStatus) - regionSetStatusSortRank(b.regionSetStatus)
      );
      /* oxlint-disable sort-keys */
      const newTableData = sortedAccessPoints.map((accessPoint) => {
        let linkHref = `${window.customUIAbsolutePath}/projects/${projectContext.project.projectId}/adequacy`;
        if (!!accessPoint.countySSA)
          linkHref += `/county/${accessPoint.countySSA}`;
        if (!!accessPoint.specialtyGroupCode)
          linkHref += `/specialty/${accessPoint.specialtyGroupCode}`;

        return {
          key: accessPoint.servicingAccessPointId.toString(),
          serviceArea: `${isNullOrUndefined(accessPoint.countySSA) ? "---" : accessPoint.countySSA} ${isNullOrUndefined(accessPoint.countyName) ? "---" : accessPoint.countyName}, ${isNullOrUndefined(accessPoint.servicingState) ? "---" : accessPoint.servicingState}`,
          countyStatus: RegionSetStatusToDisplay(accessPoint.regionSetStatus),
          specialty: isNullOrUndefined(accessPoint.specialtyGroupName) ? "---" : formatSpecialtyWithCode({ code: accessPoint.specialtyGroupCode, name: accessPoint.specialtyGroupName }),
          address: (
            <div>
              {isNullOrUndefined(accessPoint.address) ? "---" : accessPoint.address} <br />
              {isNullOrUndefined(accessPoint.address2) ? null : <>{accessPoint.address2}<br /></>}
              {isNullOrUndefined(accessPoint.city) ? "---" : accessPoint.city}, {isNullOrUndefined(accessPoint.state) ? "---" : accessPoint.state} {isNullOrUndefined(accessPoint.zip) ? "---" : accessPoint.zip}
            </div>
          ),
          geoInfo: GeoInfoToDisplay(accessPoint.geoInfo),
          fillableGapIndicator: AccessGapTypeEnumToDisplay(accessPoint.gapType),
          servesPercent: accessPoint.servesPercent,
          impactPercent: accessPoint.impactPercent,
          gainsPercent: accessPoint.gainsPercent,
          passPercent: accessPoint.passPercent,
          projectLink: <a href={linkHref} target="_blank" rel="noreferrer">View in Adequacy <QesIcon iconName={QesIcons.ShareV4} isDecorative /></a>,
        }
      });
      /* oxlint-enable sort-keys */
      setLoading(false);
      setTableData(newTableData);
    });
  }, []);

  return (
    <>
      {loading ? <QesIcon iconName={QesIcons.LoadingV1} className="qes-icon-spin" description="Loading" /> : <ProviderProfileFlyoutTable data={tableData} gapType={props.gapType} />}
    </>
  );
}
